/** Pure rules for the schedule form and the paused-state copy, kept out of
 * the components so they can be tested without rendering. */
import type { BotTaskDeliverable } from '../../../services/BotTasksService';
import type { BotSchedule, BotScheduleInput, ScheduleDelivery, ScheduleFrequency } from '../../../services/BotTaskSchedulesService';

import { isDeliverable } from './deliverable';

export interface ScheduleDraft {
  prompt: string;
  frequency: ScheduleFrequency;
  weekdays: number[];
  monthDay: number;
  time: string;
  deliverTo: ScheduleDelivery;
  deliverable: BotTaskDeliverable | '';
}

export const PROMPT_MAX = 8000;
export const SELECT_CLASS = 'h-9 w-full rounded-md border border-separator bg-background px-2 text-base lg:text-sm cursor-pointer';
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
export const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
];

export const emptyDraft = (): ScheduleDraft => ({ prompt: '', frequency: 'weekly', weekdays: [1], monthDay: 1, time: '08:00', deliverTo: 'chat', deliverable: '' });

export const toScheduleDraft = (schedule: BotSchedule): ScheduleDraft => ({
  prompt: schedule.prompt ?? '',
  frequency: schedule.frequency,
  weekdays: [...schedule.weekdays].sort((a, b) => a - b),
  monthDay: schedule.monthDay ?? 1,
  time: schedule.time,
  deliverTo: schedule.deliverTo,
  deliverable: isDeliverable(schedule.deliverable) ? schedule.deliverable : '',
});

export const sameScheduleDraft = (a: ScheduleDraft, b: ScheduleDraft) =>
  a.prompt === b.prompt && a.frequency === b.frequency && a.time === b.time && a.deliverTo === b.deliverTo && a.deliverable === b.deliverable
  && a.monthDay === b.monthDay && a.weekdays.length === b.weekdays.length && a.weekdays.every((day, i) => day === b.weekdays[i]);

/** Weekdays and the day of the month are sent only for the cadence that uses them. */
export function draftToPayload(draft: ScheduleDraft): BotScheduleInput {
  return {
    prompt: draft.prompt.trim(),
    frequency: draft.frequency,
    ...(draft.frequency === 'weekly' ? { weekdays: [...draft.weekdays].sort((a, b) => a - b) } : {}),
    ...(draft.frequency === 'monthly' ? { monthDay: draft.monthDay } : {}),
    time: draft.time,
    deliverTo: draft.deliverTo,
    ...(draft.deliverable ? { deliverable: draft.deliverable } : {}),
  };
}

/** Only what changed, so a prompt-only edit does not make the server
 * recompute the next run. A new cadence carries the days it needs. */
export function draftPatch(draft: ScheduleDraft, baseline: ScheduleDraft): Partial<BotScheduleInput> {
  const full = draftToPayload(draft);
  const patch: Partial<BotScheduleInput> = {};
  if (draft.prompt.trim() !== baseline.prompt.trim()) patch.prompt = full.prompt;
  const frequencyChanged = draft.frequency !== baseline.frequency;
  if (frequencyChanged) patch.frequency = full.frequency;
  const daysChanged = draft.weekdays.length !== baseline.weekdays.length || draft.weekdays.some((day, n) => day !== baseline.weekdays[n]);
  if (draft.frequency === 'weekly' && (frequencyChanged || daysChanged)) patch.weekdays = full.weekdays;
  if (draft.frequency === 'monthly' && (frequencyChanged || draft.monthDay !== baseline.monthDay)) patch.monthDay = full.monthDay;
  if (draft.time !== baseline.time) patch.time = full.time;
  if (draft.deliverTo !== baseline.deliverTo) patch.deliverTo = full.deliverTo;
  if (draft.deliverable !== baseline.deliverable) patch.deliverable = draft.deliverable || null;
  return patch;
}

export function draftProblem(draft: ScheduleDraft): string | null {
  const prompt = draft.prompt.trim();
  if (!prompt) return 'Say what the bot should do.';
  if (prompt.length > PROMPT_MAX) return `Keep the task under ${PROMPT_MAX} characters.`;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.time)) return 'Pick a time.';
  if (draft.frequency === 'weekly' && draft.weekdays.length === 0) return 'Pick at least one day.';
  if (draft.frequency === 'monthly' && (draft.monthDay < 1 || draft.monthDay > 28)) return 'Pick a day from 1 to 28.';
  return null;
}

export const toggleWeekday = (days: number[], day: number) =>
  (days.includes(day) ? days.filter((d) => d !== day) : [...days, day]).sort((a, b) => a - b);

export interface PausedNotice { text: string; canResume: boolean; hint: string | null }

/** Why a schedule is paused, in plain words, and whether this viewer can fix
 * it by resuming (the server still has the last word). The owner resumes
 * their own pauses, failures, a disabled bot and lost access, but never an
 * admin's pause, even if they are an admin themselves. Another admin resumes
 * only an admin's pause. `asAdmin` is true for workspace admins. */
export function pausedNotice(schedule: BotSchedule, asAdmin: boolean): PausedNotice | null {
  if (schedule.status !== 'paused') return null;
  const mine = schedule.isOwner;
  const reason = schedule.pausedReason;
  const canResume = mine ? reason !== 'admin' : asAdmin && reason === 'admin';
  switch (reason) {
    case 'admin':
      return { text: 'Paused by an admin', canResume, hint: canResume ? null : mine ? 'An admin has to resume it.' : null };
    case 'owner_no_access':
      return {
        text: mine ? 'Paused: you no longer have access to this workspace' : 'Paused: the owner no longer has access to this workspace',
        canResume, hint: mine ? 'Once you have access again, resume it.' : null,
      };
    case 'bot_disabled':
      return { text: 'Paused: this bot is disabled', canResume, hint: mine ? 'Enable the bot first, then resume.' : null };
    case 'failures':
      return { text: 'Paused after repeated failures', canResume, hint: null };
    default:
      return { text: mine ? 'Paused' : 'Paused by the owner', canResume, hint: null };
  }
}

/** Run now is for the owner while the schedule is not blocked by an admin,
 * lost access or a disabled bot. */
export const canRunNow = (schedule: BotSchedule) =>
  schedule.isOwner && (schedule.status === 'active' || schedule.pausedReason === 'owner' || schedule.pausedReason === 'failures');

/** "Mon 6 Oct, 08:00", 24-hour, UK order. */
export const formatNextRun = (iso: string | null, timezone?: string) =>
  iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, ...(timezone ? { timeZone: timezone } : {}) }) : null;
